import 'react-app-polyfill/ie11';
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import React from 'react';
import ReactDOM from 'react-dom';
import * as Sentry from '@sentry/react';
import { Provider } from 'react-redux';

import SeriesSlider from 'components/blog/SeriesSlider';
import PrettyQuote from 'components/blog/PrettyQuote';

import loadMore from 'utils/load_more';
import { filterOptions as sentryFilterOptions } from 'utils/sentry';

import rootReducer from './reducers';
import configureStore from './store';
import ActorsCarousel from './components/ActorsCarousel';
import ImageCarousel from './components/ImageCarousel';
import ManagementContainer from './components/Management/ManagementContainer';
import RestaurantMenu from './components/RestaurantMenu';
import { BlogGalleryLink, StagingGallery } from './components/Gallery';
import HomePage from './components/HomePage';
import SearchForm from './components/SearchForm';
import SubscribeForm from './components/blog/SubscribeForm';
import AlertWindow from './components/AlertWindow';

// Configure Sentry in deployed envs
if (process.env.NODE_ENV === 'production') {
    Sentry.init({
        dsn: DJ_CONST.SENTRY_DSN,
        environment: DJ_CONST.SENTRY_ENVIRONMENT,
        ...sentryFilterOptions,
        integrations: [
            Sentry.replayIntegration({
                maskAllText: false,
            }),
        ],
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
    });

    // If we have authenticated user, pass its data on to Sentry
    if (DJ_CONST.user) {
        Sentry.configureScope(scope => {
            scope.setUser({
                id: DJ_CONST.user.id,
                email: DJ_CONST.user.email,
            });
            Sentry.setContext('name', DJ_CONST.user.name);
        });
    }
}

function quotes() {
    const article = document.getElementById('post');
    if (!article) {
        return;
    }

    const tweetQuotes = article.getElementsByClassName('tweet-quote');
    if (tweetQuotes.length === 0) {
        return;
    }

    Array.prototype.forEach.call(tweetQuotes, quote => {
        const quoteText = quote.innerText;
        ReactDOM.render(<PrettyQuote content={quoteText} />, quote);
    });
}

function makeTablesScrollable() {
    function wrap(el, wrapper) {
        if (el.parentNode !== null) {
            el.parentNode.insertBefore(wrapper, el);
            wrapper.appendChild(el);
        }
    }

    // get all the tables within CMS plugin
    const tables = document.querySelectorAll('div.cms-content > table');
    if (tables !== null) {
        // wrap tables with a scrollable wrapper
        for (let i = 0; i < tables.length; i++) {
            // create a scrollable wrapper
            const tableWrapper = document.createElement('div');
            tableWrapper.style.cssText = 'overflow-x:auto';
            tableWrapper.id += `table-wrapper-${i}`;
            wrap(tables[i], tableWrapper);
        }
    }
}

function addLoadMoreListener() {
    document.addEventListener('click', e => {
        if (e.target.id === 'load-more') {
            loadMore(e.target);
        }
    });
}

function addSeriesSlider() {
    const slider = document.getElementById('series-slider-area');
    if (!slider) {
        return;
    }

    ReactDOM.render(<SeriesSlider data={slider.dataset.children} />, slider);
}

function initSubscribeForm() {
    const subscribeElem = document.getElementById('newsletter-section-footer');
    if (!subscribeElem) {
        return;
    }
    //  get data-language attribute from the subscribe form element and pass it to the component as a prop
    const language = subscribeElem.dataset.language || 'et';

    ReactDOM.render(
        <SubscribeForm
            siteKey={DJ_CONST.RECAPTCHA_PUBLIC_SITE_KEY}
            language={language}
        />,
        subscribeElem,
    );
}

function initBlog() {
    quotes();
    addLoadMoreListener();
    addSeriesSlider();
}

// Create Redux store
const store = configureStore(rootReducer);

function initHome(artistsData) {
    const elem = document.querySelector('.container__actors');
    if (elem) {
        ReactDOM.render(<ActorsCarousel artists={artistsData} />, elem);
    }

    const wrap = document.getElementById('showsselector');
    if (wrap) {
        ReactDOM.render(
            <Provider store={store}>
                <HomePage />
            </Provider>,
            wrap,
        );
    }

    const searchForm = document.getElementById('search-input-form');
    if (searchForm) {
        ReactDOM.render(<SearchForm id="search-input" />, searchForm);
    }
    const navSearchForm = document.getElementById('nav-search-input-form');
    if (navSearchForm) {
        ReactDOM.render(<SearchForm id="nav-search-input" />, navSearchForm);
    }
}

function initRestaurant() {
    const imgElem = document.querySelector('.gallery-carousel--short');
    if (!imgElem) {
        return;
    }

    ReactDOM.render(<ImageCarousel />, imgElem);
}

function initManagement() {
    const elem = document.getElementById('management-container');

    if (!elem) {
        return;
    }

    ReactDOM.render(<ManagementContainer />, elem);
}

function initRestaurantMenu() {
    const elem = document.querySelector('.menu');

    if (!elem) {
        return;
    }

    ReactDOM.render(
        <Provider store={store}>
            <RestaurantMenu key="menu" />
        </Provider>,
        elem,
    );
}

function initBlogGallery() {
    const elem = document.querySelector('.masthead__gallery-link');

    if (!elem || !elem.dataset.gallery) {
        return;
    }

    let data;

    try {
        data = JSON.parse(elem.dataset.gallery);
    } catch (e) {
        return;
    }

    ReactDOM.render(<BlogGalleryLink data={data} />, elem);
}

function initGallery() {
    const elem = document.getElementById('gallery');

    if (!elem || !elem.dataset.gallery) {
        return;
    }

    let data;

    try {
        data = JSON.parse(elem.dataset.gallery);
    } catch (e) {
        return;
    }

    if (data) {
        ReactDOM.render(<StagingGallery data={data} />, elem);
    }
}

function runOnScroll() {
    // Header opacity stuff
    const fixedHeader = document.querySelector('.nav-wrap');

    if (document.documentElement.scrollTop > 108) {
        fixedHeader.classList.add('sticky');
    } else {
        fixedHeader.classList.remove('sticky');
    }
}

function initStickyMenu() {
    window.addEventListener('scroll', runOnScroll);
}

function initAlert() {
    const elem = document.getElementById('alert_window');

    if (!elem || !elem.dataset) {
        return;
    }

    const container = document.getElementById('alert-container');
    if (!container) {
        return;
    }

    let data;

    try {
        data = elem.dataset;
    } catch (e) {
        return;
    }
    if (data) {
        ReactDOM.render(<AlertWindow data={data} />, container);
        // Remove the html element rendered by CMS plugin and we extracted all the data from it
        elem.remove();
    }
}

function initMobileMenuAccessibility() {
    const mobileMenuToggle = document.querySelector('.navbar-toggler');
    const mobileMenu = document.querySelector('#headerNavigation');
    const mobileMenuCloseBtn = document.querySelector(
        '.offcanvas-collapse__close-btn',
    );
    const { body } = document;

    if (!mobileMenuToggle || !mobileMenu) {
        return;
    }

    // Store the original scroll position
    let scrollPosition = 0;
    let focusTrapHandler = null;

    // Function to get all focusable elements in the menu
    function getFocusableElements() {
        return mobileMenu.querySelectorAll(
            'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"]), [role="button"]',
        );
    }

    // Function to trap focus within the mobile menu
    function createFocusTrap() {
        focusTrapHandler = function(e) {
            const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

            if (!isTabPressed) {
                return;
            }

            const focusableElements = getFocusableElements();
            const firstFocusableElement = focusableElements[0];
            const lastFocusableElement =
                focusableElements[focusableElements.length - 1];

            if (e.shiftKey) {
                // Shift + Tab
                if (document.activeElement === firstFocusableElement) {
                    lastFocusableElement.focus();
                    e.preventDefault();
                }
            } else if (document.activeElement === lastFocusableElement) {
                // Tab
                firstFocusableElement.focus();
                e.preventDefault();
            }
        };

        document.addEventListener('keydown', focusTrapHandler);
    }

    // Function to remove focus trap
    function removeFocusTrap() {
        if (focusTrapHandler) {
            document.removeEventListener('keydown', focusTrapHandler);
            focusTrapHandler = null;
        }
    }

    // Function to disable background elements
    function disableBackgroundElements() {
        const allElements = document.querySelectorAll(
            'body *:not(#headerNavigation):not(#headerNavigation *)',
        );
        allElements.forEach(paramEl => {
            const el = paramEl;
            if (el.tabIndex >= 0 || el.hasAttribute('tabindex')) {
                el.setAttribute('data-original-tabindex', el.tabIndex);
                el.tabIndex = -1;
            }
            if (
                el.tagName === 'A' ||
                el.tagName === 'BUTTON' ||
                el.tagName === 'INPUT' ||
                el.tagName === 'SELECT' ||
                el.tagName === 'TEXTAREA'
            ) {
                if (!el.hasAttribute('data-original-tabindex')) {
                    el.setAttribute('data-original-tabindex', el.tabIndex || 0);
                    el.tabIndex = -1;
                }
            }
        });
    }

    // Function to hide background from screen readers
    function hideBackgroundFromScreenReaders() {
        const allElements = document.querySelectorAll('body *');

        allElements.forEach(paramEl => {
            const el = paramEl;
            if (
                el === mobileMenu ||
                mobileMenu.contains(el) ||
                el.contains(mobileMenu)
            ) {
                return;
            }
            if (el.hasAttribute('aria-hidden')) {
                el.setAttribute(
                    'data-original-aria-hidden',
                    el.getAttribute('aria-hidden'),
                );
            } else {
                el.setAttribute('data-mobile-menu-aria-hidden', 'true');
            }
            el.setAttribute('aria-hidden', 'true');
        });

        // Force-hide known navigation elements outside the offcanvas menu.
        // Not happy with this solution but it does work for now
        const forcedHiddenSelectors = [
            '.nav-mobile > ul.menu-top',
            '.nav-desktop',
            '.language-switcher',
            '.navbar-brand',
            '.navbar-toggler',
            '.skip-to-content',
        ];

        document
            .querySelectorAll(forcedHiddenSelectors.join(','))
            .forEach(paramEl => {
                const el = paramEl;
                if (el === mobileMenu || mobileMenu.contains(el)) {
                    return;
                }
                if (!el.hasAttribute('aria-hidden')) {
                    el.setAttribute('data-mobile-menu-aria-hidden', 'true');
                } else if (!el.hasAttribute('data-original-aria-hidden')) {
                    el.setAttribute(
                        'data-original-aria-hidden',
                        el.getAttribute('aria-hidden'),
                    );
                }
                el.setAttribute('aria-hidden', 'true');
            });
    }

    // Function to restore screen reader visibility for background
    function restoreBackgroundForScreenReaders() {
        const allElements = document.querySelectorAll(
            '[data-mobile-menu-aria-hidden], [data-original-aria-hidden]',
        );
        allElements.forEach(paramEl => {
            const el = paramEl;
            if (el.hasAttribute('data-original-aria-hidden')) {
                el.setAttribute(
                    'aria-hidden',
                    el.getAttribute('data-original-aria-hidden'),
                );
                el.removeAttribute('data-original-aria-hidden');
            } else if (el.hasAttribute('data-mobile-menu-aria-hidden')) {
                el.removeAttribute('aria-hidden');
                el.removeAttribute('data-mobile-menu-aria-hidden');
            }
        });
    }

    // Function to restore background elements
    function restoreBackgroundElements() {
        const allElements = document.querySelectorAll(
            '[data-original-tabindex]',
        );
        allElements.forEach(paramEl => {
            const el = paramEl;
            const originalTabIndex = el.getAttribute('data-original-tabindex');
            if (originalTabIndex === '0' || originalTabIndex === '') {
                el.removeAttribute('tabindex');
            } else {
                el.tabIndex = parseInt(originalTabIndex, 10);
            }
            el.removeAttribute('data-original-tabindex');
        });
    }

    // Function to open mobile menu
    function openMobileMenu() {
        // Store current scroll position
        scrollPosition = window.pageYOffset;

        // Add class to prevent body scrolling
        body.classList.add('mobile-menu-open');
        body.style.top = `-${scrollPosition}px`;

        // Set ARIA attributes
        mobileMenuToggle.setAttribute('aria-expanded', 'true');
        mobileMenu.setAttribute('aria-hidden', 'false');

        // Disable background elements and create focus trap
        disableBackgroundElements();
        hideBackgroundFromScreenReaders();
        createFocusTrap();

        // Focus the close button
        setTimeout(() => {
            const closeBtn = mobileMenu.querySelector(
                '.offcanvas-collapse__close-btn',
            );
            if (closeBtn) {
                closeBtn.focus();
            }
        }, 100);
    }

    // Function to close mobile menu
    function closeMobileMenu() {
        // Remove class to restore body scrolling
        body.classList.remove('mobile-menu-open');
        body.style.top = '';

        // Restore scroll position
        window.scrollTo(0, scrollPosition);

        // Set ARIA attributes
        mobileMenuToggle.setAttribute('aria-expanded', 'false');
        mobileMenu.setAttribute('aria-hidden', 'true');

        // Restore background elements and remove focus trap
        restoreBackgroundElements();
        restoreBackgroundForScreenReaders();
        removeFocusTrap();

        // Return focus to the toggle button
        mobileMenuToggle.focus();
    }

    function requestCloseMobileMenu() {
        if (!mobileMenu.classList.contains('show')) {
            return;
        }

        if (window.bootstrap && window.bootstrap.Collapse) {
            const collapse = window.bootstrap.Collapse.getOrCreateInstance(
                mobileMenu,
            );
            collapse.hide();
            return;
        }

        mobileMenu.classList.remove('show');
        closeMobileMenu();
    }

    // Listen for Bootstrap collapse events
    mobileMenu.addEventListener('show.bs.collapse', openMobileMenu);
    mobileMenu.addEventListener('hide.bs.collapse', closeMobileMenu);

    // Handle keyboard events for close button
    if (mobileMenuCloseBtn) {
        mobileMenuCloseBtn.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            requestCloseMobileMenu();
        });
        mobileMenuCloseBtn.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                requestCloseMobileMenu();
            }
        });
    }

    // Handle escape key to close menu
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && mobileMenu.classList.contains('show')) {
            requestCloseMobileMenu();
        }
    });
}

// Opens the accordion item targeted by the current URL hash (e.g. a job
// posting linked as `.../#trompetimangija-steven`). The accordion_item.html
// template emits a slug `<span id>` anchor; the `#item-<pk>` collapse id also
// works. Runs on load and on hashchange.
function openAccordionFromHash() {
    const { hash } = window.location;
    if (!hash || hash.length < 2) {
        return;
    }

    // getElementById (not querySelector) so ids that aren't valid CSS
    // selectors — e.g. starting with a digit — still resolve.
    let id;
    try {
        id = decodeURIComponent(hash.slice(1));
    } catch (e) {
        return; // malformed percent-encoding in the hash
    }
    const target = document.getElementById(id);
    if (!target) {
        return;
    }

    const item = target.closest('.accordion-item');
    if (!item) {
        return;
    }

    // Leave room for the sticky site header, value set arbitrarily what looked ok
    const HEADER_OFFSET = 325;
    const computeTop = () =>
        item.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    const scrollToItem = () => {
        window.scrollTo({ top: computeTop(), behavior: 'smooth' });
        setTimeout(() => {
            window.scrollTo({ top: computeTop(), behavior: 'smooth' });
        }, 200);
    };

    const collapse = item.querySelector('.accordion-collapse');
    const button = item.querySelector('.accordion-button');

    // Close every other open item in the same accordion so only the targeted
    // one stays open — including items that default to open. Toggling via the
    // button keeps Bootstrap's collapse state in sync.
    const accordion = item.closest('.accordion');
    if (accordion) {
        accordion
            .querySelectorAll('.accordion-collapse.show')
            .forEach(otherCollapse => {
                if (otherCollapse === collapse) {
                    return;
                }
                const otherButton = otherCollapse
                    .closest('.accordion-item')
                    .querySelector('.accordion-button');
                if (otherButton) {
                    otherButton.click();
                }
            });
    }

    // Open the target by clicking its toggle button: Bootstrap's delegated
    // `data-bs-toggle="collapse"` handler runs even though the bundle does not
    // expose `window.bootstrap` under Webpack. Guard so an already-open target
    // is not toggled shut.
    if (button && collapse && !collapse.classList.contains('show')) {
        // Scroll only once the expand transition finishes, otherwise the
        // mid-animation height makes the target position overshoot.
        collapse.addEventListener('shown.bs.collapse', scrollToItem, {
            once: true,
        });
        button.click();
    } else {
        scrollToItem();
    }
}

function initAccordionDeepLink() {
    openAccordionFromHash();
    window.addEventListener('hashchange', openAccordionFromHash);
}

// Wires up the "copy link" buttons rendered into each accordion header by
// accordion_item.html. Clicking copies that item's deep-link URL (page URL +
// `#slug`) to the clipboard, so a non-technical editor can share a link that
// opens a specific item (handled by openAccordionFromHash on the target page).
// Success is announced through a shared aria-live region so screen-reader users
// get the same confirmation as the visual "Copied!" tooltip.
function initAccordionPermalinks() {
    const buttons = document.querySelectorAll('.accordion-permalink');
    if (!buttons.length) {
        return;
    }

    const status = document.createElement('div');
    status.className = 'visually-hidden';
    status.setAttribute('aria-live', 'polite');
    document.body.appendChild(status);

    const copyText = async text => {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return;
        }
        // Fallback for non-secure contexts / older browsers.
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
    };

    buttons.forEach(button => {
        button.addEventListener('click', async () => {
            const { permalinkSlug } = button.dataset;
            if (!permalinkSlug) {
                return;
            }

            const { origin, pathname, search } = window.location;
            const url = `${origin}${pathname}${search}#${permalinkSlug}`;

            try {
                await copyText(url);
            } catch (e) {
                return; // clipboard denied/unavailable — fail quietly
            }

            const label = button.dataset.copiedLabel || 'Link copied';
            // Clear first so an identical message re-announces on repeat clicks.
            status.textContent = '';
            window.requestAnimationFrame(() => {
                status.textContent = label;
            });

            button.classList.add('is-copied');
            window.setTimeout(() => {
                button.classList.remove('is-copied');
            }, 2000);
        });
    });
}

export {
    initHome,
    initBlog,
    initRestaurant,
    initManagement,
    initRestaurantMenu,
    initBlogGallery,
    initGallery,
    initSubscribeForm,
    makeTablesScrollable,
    initStickyMenu,
    initAlert,
    initMobileMenuAccessibility,
    initAccordionDeepLink,
    initAccordionPermalinks,
}; // eslint-disable-line import/prefer-default-export
